import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    ICreateStyleRequest,
    IStyle,
    IStyleAttachment,
    IStyleCostLineRequest,
    IStylePage,
    IUpdateStyleRequest,
    StyleDocumentKind,
    StyleStatus,
} from '../interfaces/garments/style.interface';

const headers = () => AuthService.getAuthHeader();

export const StyleService = {
    list: async (
        institutionId: string,
        page = 0,
        size = 20,
        buyerId?: string,
        status?: StyleStatus,
        search?: string,
        activeOnly?: boolean
    ): Promise<IStylePage> => {
        const res = await axios.get(ENDPOINTS.STYLE.LIST(institutionId), {
            headers: headers(),
            params: { page, size, buyerId, status, search, activeOnly },
        });
        return res.data;
    },

    getById: async (institutionId: string, id: string): Promise<IStyle> => {
        const res = await axios.get(ENDPOINTS.STYLE.GET(institutionId, id), { headers: headers() });
        return res.data;
    },

    create: async (institutionId: string, request: ICreateStyleRequest): Promise<IStyle> => {
        const res = await axios.post(ENDPOINTS.STYLE.CREATE(institutionId), request, { headers: headers() });
        return res.data;
    },

    update: async (institutionId: string, id: string, request: IUpdateStyleRequest): Promise<IStyle> => {
        const res = await axios.put(ENDPOINTS.STYLE.UPDATE(institutionId, id), request, { headers: headers() });
        return res.data;
    },

    changeStage: async (institutionId: string, id: string, status: 'DEVELOPMENT' | 'QUOTED' | 'CONFIRMED'): Promise<IStyle> => {
        const res = await axios.patch(ENDPOINTS.STYLE.STAGE(institutionId, id), { status }, { headers: headers() });
        return res.data;
    },

    archive: async (institutionId: string, id: string): Promise<IStyle> => {
        const res = await axios.patch(ENDPOINTS.STYLE.ARCHIVE(institutionId, id), null, { headers: headers() });
        return res.data;
    },

    activate: async (institutionId: string, id: string): Promise<IStyle> => {
        const res = await axios.patch(ENDPOINTS.STYLE.ACTIVATE(institutionId, id), null, { headers: headers() });
        return res.data;
    },

    replaceCostLines: async (institutionId: string, id: string, requests: IStyleCostLineRequest[]): Promise<IStyle> => {
        const res = await axios.put(
            ENDPOINTS.STYLE.REPLACE_COST_LINES(institutionId, id),
            { lines: requests },
            { headers: headers() }
        );
        return res.data;
    },

    uploadAttachment: async (
        institutionId: string,
        id: string,
        file: File,
        documentKind?: StyleDocumentKind,
        revisionLabel?: string,
        notes?: string
    ): Promise<IStyleAttachment> => {
        const formData = new FormData();
        formData.append('file', file);
        if (documentKind) formData.append('documentKind', documentKind);
        if (revisionLabel) formData.append('revisionLabel', revisionLabel);
        if (notes) formData.append('notes', notes);
        const res = await axios.post(ENDPOINTS.STYLE.UPLOAD_ATTACHMENT(institutionId, id), formData, {
            headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
    },

    listAttachments: async (institutionId: string, id: string, documentKind?: StyleDocumentKind): Promise<IStyleAttachment[]> => {
        const res = await axios.get(ENDPOINTS.STYLE.LIST_ATTACHMENTS(institutionId, id), {
            headers: headers(),
            params: documentKind ? { documentKind } : undefined,
        });
        return res.data;
    },

    downloadAttachment: async (institutionId: string, id: string, attachmentId: string, fileName: string): Promise<void> => {
        const res = await axios.get(ENDPOINTS.STYLE.DOWNLOAD_ATTACHMENT(institutionId, id, attachmentId), {
            headers: headers(),
            responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    deleteAttachment: async (institutionId: string, id: string, attachmentId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.STYLE.DELETE_ATTACHMENT(institutionId, id, attachmentId), { headers: headers() });
    },
};
